import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import {
  AddButton,
  ActionButtons,
  DataTable,
  Navigation,
  Panel,
  Container,
} from '../../components';
import { THEME } from '../../../constants/styles';
import { appointmentLabelForType } from '../scheduler/common_views';
import {
  deleteSchedulerNotificationMessage as deleteSchedulerNotificationMessageAction,
  getSchedulerNotificationMessages as getSchedulerNotificationMessagesAction,
  resetState,
} from './slice';
import { REQUESTING } from '../../../actions/statusTypes';
import { canEdit, canView } from '../../../helpers/permissions';
import { BASE_SETUP_NOTIFICATIONS } from '../../../constants/permissions';

function trimText(value) {
  return value?.trim();
}

function isDefaultSchedulerNotificationMessage(message) {
  return (
    !message?.appointmentType && message?.masterDepartmentId == null && !trimText(message?.reason)
  );
}

function rowKey(item) {
  return item.schedulerMessageId;
}

export default memo(function SchedulerNotificationMessages(props) {
  const employerId = props.employer?.employerId;
  const dispatch = useDispatch();
  const occHealthDepartments = useSelector(
    (state) => state.screens.employer.occHealthDepartmentsData
  );
  const messages = useSelector(
    (state) => state.screens.airforceSetup.notificationMessagesData || []
  );
  const notificationMessagesStatus = useSelector(
    (state) => state.screens.airforceSetup.notificationMessagesStatus
  );
  const deleteNotificationMessageStatus = useSelector(
    (state) => state.screens.airforceSetup.deleteNotificationMessageStatus
  );
  const notificationMessagesError = useSelector(
    (state) => state.screens.airforceSetup.notificationMessagesError
  );
  const deleteNotificationMessageError = useSelector(
    (state) => state.screens.airforceSetup.deleteNotificationMessageError
  );

  const canEditPanel = canEdit(props.permissions, BASE_SETUP_NOTIFICATIONS);
  const canViewNotifications = canView(props.permissions, BASE_SETUP_NOTIFICATIONS);
  const isLoading = notificationMessagesStatus === REQUESTING;
  const isMutating = deleteNotificationMessageStatus === REQUESTING;

  useEffect(() => {
    dispatch(getSchedulerNotificationMessagesAction({ employerId }));
    return () => {
      dispatch(resetState());
    };
  }, [dispatch, employerId]);

  const departmentLabelLookup = useMemo(() => {
    return Object.values(occHealthDepartments || {}).reduce((result, department) => {
      if (department?.master_department_id && department?.name) {
        result[department.master_department_id] = department.name;
      }

      return result;
    }, {});
  }, [occHealthDepartments]);

  const rowData = useMemo(() => {
    return messages
      .map((message) => ({
        ...message,
        key: rowKey(message),
        appointmentTypeLabel: message.appointmentType
          ? appointmentLabelForType(message.appointmentType)
          : 'All appointment types',
        reasonLabel: trimText(message.reason) || 'Any reason',
        masterDepartmentLabel:
          message.masterDepartmentId != null
            ? departmentLabelLookup[message.masterDepartmentId] ||
              `Department ${message.masterDepartmentId}`
            : 'All departments',
      }))
      .sort((a, b) => {
        return [a.appointmentTypeLabel, a.reasonLabel, a.masterDepartmentLabel]
          .join(' ')
          .localeCompare(
            [b.appointmentTypeLabel, b.reasonLabel, b.masterDepartmentLabel].join(' ')
          );
      });
  }, [departmentLabelLookup, messages]);

  const openEditorModal = useCallback(
    (mode, item = null) => {
      Navigation.showModal({
        screen: 'SchedulerNotificationMessagesForm',
        title: mode === 'edit' ? 'Edit notification messages' : 'Add notification messages',
        passProps: {
          employer: props.employer,
          item,
          mode,
        },
        options: {
          containerStyle: {
            maxWidth: 1600,
          },
        },
      });
    },
    [dispatch, props.employer]
  );
  const handlePreviewPress = useCallback(
    (item) => {
      Navigation.showModal({
        screen: 'SchedulerNotificationPreview',
        title: 'Message Preview',
        passProps: {
          item,
          employerId,
        },
        options: {
          containerStyle: {
            maxWidth: 1200,
          },
        },
      });
    },
    [dispatch, employerId]
  );

  const handleAddPress = useCallback(() => {
    openEditorModal('add');
  }, [openEditorModal]);

  const handleEditPress = useCallback(
    (item) => {
      openEditorModal('edit', item);
    },
    [openEditorModal]
  );

  const canDeleteItem = useCallback(
    (item) => {
      return (
        canEditPanel && !isDefaultSchedulerNotificationMessage(item) && !!item?.schedulerMessageId
      );
    },
    [canEditPanel]
  );

  const handleDelete = useCallback(
    (item) => {
      dispatch(
        deleteSchedulerNotificationMessageAction({
          employerId,
          schedulerMessageId: item.schedulerMessageId,
        })
      );
    },
    [dispatch, employerId]
  );

  const handleDeletePress = useCallback(
    (item) => {
      props.onConfirmationOpts({
        title: 'Delete this notification message?',
        description: `This will delete the messages for ${item.appointmentTypeLabel}, ${item.masterDepartmentLabel}, ${item.reasonLabel}.`,
        confirm: 'Yes, Delete',
        onConfirmed: () => {
          handleDelete(item);
        },
      });
    },
    [handleDelete, props.onConfirmationOpts]
  );

  const columns = useMemo(() => {
    return [
      {
        headerLabel: 'APPOINTMENT TYPE',
        style: { width: 150, flexGrow: 1 },
        extraRowStyle: styles.shortRow,
        dataAccessor: 'appointmentTypeLabel',
      },
      {
        headerLabel: 'DEPARTMENT',
        style: { width: 150, flexGrow: 1 },
        extraRowStyle: styles.shortRow,
        dataAccessor: 'masterDepartmentLabel',
      },
      {
        headerLabel: 'REJECTION REASON',
        style: { width: 150, flexGrow: 1 },
        extraRowStyle: styles.shortRow,
        dataAccessor: 'reasonLabel',
      },
      {
        headerLabel: 'ACTIONS',
        style: { width: 100, flexGrow: 1, flexShrink: 0 },
        extraRowStyle: styles.shortRow,
        dataAccessor: (item) => (
          <ActionButtons
            item={item}
            onRejectPress={canDeleteItem(item) ? handleDeletePress : null}
            rejectDisabled={isMutating}
            rejectButtonTestId={
              item.schedulerMessageId != null
                ? `scheduler-notification-delete-${item.schedulerMessageId}`
                : undefined
            }
            onPreviewPress={handlePreviewPress}
            previewDisabled={!canViewNotifications || isMutating}
            onEditPress={canEditPanel ? handleEditPress : null}
            editDisabled={!canEditPanel || isMutating}
          />
        ),
      },
    ];
  }, [
    canDeleteItem,
    canEditPanel,
    canViewNotifications,
    handleDeletePress,
    handleEditPress,
    handlePreviewPress,
    isMutating,
  ]);

  return (
    <Panel
      title="Notification Messages"
      containerStyle={styles.panel}
      extraHeaderContent={
        canEditPanel ? (
          <AddButton
            label="ADD"
            onPress={handleAddPress}
            containerStyle={{ height: 40, paddingRight: 10 }}
          />
        ) : null
      }
    >
      <Container
        errorMessage={
          deleteNotificationMessageError?.message || notificationMessagesError?.message || null
        }
        renderActivityOverlay={!!isLoading || !!isMutating}
      >
        <DataTable columns={columns} data={rowData} altColors={true} />
        {!isLoading && rowData.length === 0 && (
          <View style={styles.emptyStateContainer}>
            <Text style={THEME.textRegular}>No notification messages have been configured.</Text>
          </View>
        )}
      </Container>
    </Panel>
  );
});

const styles = StyleSheet.create({
  panel: {
    minWidth: 820,
  },
  shortRow: {
    paddingVertical: 1,
  },
  emptyStateContainer: {
    paddingVertical: 25,
    paddingHorizontal: 15,
  },
});
